import React, { useState, useEffect } from 'react'
import {
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material'
import {
  CheckCircle,
  Psychology,
  Speed,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { adminAPI } from '../services/api'
import AdminPageWrapper from '../components/AdminPageWrapper'

const AdminModelsPage = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modelsData, setModelsData] = useState(null)
  const [selectedPrimary, setSelectedPrimary] = useState('')
  const [selectedSimple, setSelectedSimple] = useState('')
  const [selectedFallback, setSelectedFallback] = useState('')
  const [saveStatus, setSaveStatus] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchModelsData()
  }, [])

  const fetchModelsData = async () => {
    try {
      setLoading(true)
      setError('')

      const response = await adminAPI.getAvailableLLMModels()
      setModelsData(response)
      setSelectedPrimary(response.primary_default || '')
      setSelectedSimple(response.simple_default || '')
      setSelectedFallback(response.fallback_default || '')
    } catch (err) {
      setError(`Failed to load models data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveDefaults = async () => {
    setSaving(true)
    setSaveStatus('')
    try {
      const updated = await adminAPI.updateLLMDefaults({
        primary_default: selectedPrimary || undefined,
        simple_default: selectedSimple || undefined,
        fallback_default: selectedFallback || undefined,
      })
      setSaveStatus('success')
      // Update modelsData directly from the save response to avoid a stale re-fetch
      setModelsData((prev) => ({ ...prev, ...updated }))
    } catch (err) {
      setSaveStatus('error')
    } finally {
      setSaving(false)
    }
  }

  const getProviderColor = (provider) => {
    switch (provider?.toLowerCase()) {
      case 'openai':
        return 'success'
      case 'azure':
        return 'info'
      default:
        return 'default'
    }
  }

  const getProviderIcon = (provider) => {
    switch (provider?.toLowerCase()) {
      case 'openai':
        return <Psychology />
      case 'azure':
        return <SettingsIcon />
      default:
        return <SettingsIcon />
    }
  }

  if (loading) {
    return (
      <AdminPageWrapper title="LLM Models">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminPageWrapper>
    )
  }

  if (error) {
    return (
      <AdminPageWrapper title="LLM Models">
        <Alert severity="error">{error}</Alert>
      </AdminPageWrapper>
    )
  }

  return (
    <AdminPageWrapper
      title="LLM Models"
      subtitle="View all configured LLM models available for agent execution. Models are configured in /config/{environment}.json files."
    >

      {/* Models Summary */}
      {modelsData && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuration Summary
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Models
                    </Typography>
                    <Typography variant="h6">
                      {modelsData.total || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Default Model
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      {modelsData.default_model || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Primary Default
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      {modelsData.primary_default || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Simple Default
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      {modelsData.simple_default || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Primary Default:</strong> Default model used for primary agent operations (complex reasoning, decision-making).
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Simple Default:</strong> Default model used for simple operations (templating, formatting, quick tasks).
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Fallback Default:</strong> Model used automatically when the primary model returns a 429 (rate limit) error.
              </Typography>
            </Alert>
          </Paper>

          {/* Models Table */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Available Models ({modelsData.models?.length || 0})
            </Typography>

            {modelsData.models && modelsData.models.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Service ID</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Configuration</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modelsData.models.map((model) => (
                      <TableRow key={model.service_id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getProviderIcon(model.provider)}
                            <Typography variant="body2" fontWeight="bold">
                              {model.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {model.service_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={model.provider}
                            size="small"
                            color={getProviderColor(model.provider)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {model.model_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {model.configuration && (
                            <Box>
                              {model.provider.toLowerCase() === 'openai' && (
                                <>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    API Key: {model.configuration.api_key_configured ? (
                                      <Chip label={model.configuration.api_key} size="small" color="success" sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} />
                                    ) : (
                                      <Chip label="Not configured" size="small" color="error" sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} />
                                    )}
                                  </Typography>
                                  {model.configuration.org_id && model.configuration.org_id !== 'Not set' && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Org ID: {model.configuration.org_id}
                                    </Typography>
                                  )}
                                </>
                              )}
                              {model.provider.toLowerCase() === 'azure' && (
                                <>
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                    {model.configuration.endpoint}
                                  </Typography>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    API: {model.configuration.api_version}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {model.is_default && (
                              <Chip
                                icon={<CheckCircle />}
                                label="Default"
                                size="small"
                                color="success"
                              />
                            )}
                            {model.service_id === modelsData.primary_default && (
                              <Chip
                                icon={<Psychology />}
                                label="Primary"
                                size="small"
                                color="primary"
                              />
                            )}
                            {model.service_id === modelsData.simple_default && (
                              <Chip
                                icon={<Speed />}
                                label="Simple"
                                size="small"
                                color="secondary"
                              />
                            )}
                            {model.service_id === modelsData.fallback_default && (
                              <Chip
                                icon={<WarningIcon />}
                                label="Fallback"
                                size="small"
                                color="warning"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="warning">
                No models configured. Please add models to your /config/{'{'} environment {'}'}.json file.
              </Alert>
            )}

            {/* Set Default Models */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Set Default Models
              </Typography>
              <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Primary Default</InputLabel>
                  <Select
                    value={selectedPrimary}
                    label="Primary Default"
                    onChange={(e) => setSelectedPrimary(e.target.value)}
                  >
                    {modelsData.models?.map((m) => (
                      <MenuItem key={m.service_id} value={m.service_id}>{m.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Simple Default</InputLabel>
                  <Select
                    value={selectedSimple}
                    label="Simple Default"
                    onChange={(e) => setSelectedSimple(e.target.value)}
                  >
                    {modelsData.models?.map((m) => (
                      <MenuItem key={m.service_id} value={m.service_id}>{m.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Fallback (on 429)</InputLabel>
                  <Select
                    value={selectedFallback}
                    label="Fallback (on 429)"
                    onChange={(e) => setSelectedFallback(e.target.value)}
                  >
                    {modelsData.models?.map((m) => (
                      <MenuItem key={m.service_id} value={m.service_id}>{m.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveDefaults}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Changes take effect immediately on this pod. Other running pods sync every 30 seconds — allow up to 30s for all instances to reflect the new defaults.
              </Alert>
              {saveStatus === 'success' && (
                <Alert severity="success" sx={{ mt: 1 }}>Default models updated successfully.</Alert>
              )}
              {saveStatus === 'error' && (
                <Alert severity="error" sx={{ mt: 1 }}>Failed to update default models.</Alert>
              )}
            </Box>
          </Paper>

          {/* Model Configuration Help */}
          <Paper sx={{ p: 3 }}>
            <Box>
              <Alert severity="info">
                <Typography variant="subtitle2" gutterBottom>
                  How to Configure Models:
                </Typography>
                <Typography variant="body2" component="div">
                  Models are configured in <code>{'/config/{environment}.json'}</code>. Example:
                  <pre style={{ fontSize: '0.8rem', overflow: 'auto', marginTop: '8px', background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
{`{
  "ai_models": [
    {
      "name": "OpenAI_Gpt_5",
      "provider": "OpenAI",
      "model": "gpt-5",
      "is_default": true
    },
    {
      "name": "Azure_Chat4o",
      "provider": "Azure",
      "endpoint": "https://your-endpoint.openai.azure.com/",
      "model": "gpt-4o",
      "api_version": "2024-08-01-preview"
    }
  ],
  "agent_defaults": {
    "primary_default": "OpenAI_Gpt_5",
    "simple_default": "Azure_Chat4o"
  }
}`}
                  </pre>
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </>
      )}
    </AdminPageWrapper>
  )
}

export default AdminModelsPage
